import { Injectable } from '@angular/core';

interface StorageEntry {
  key: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class IndexedDbStorageService {
  private readonly databaseName = 'tops-offline-storage';
  private readonly storeName = 'entries';
  private readonly cache = new Map<string, string>();
  private readonly databasePromise = this.openDatabase();
  private readonly readyPromise = this.initialize();

  ready(): Promise<void> {
    return this.readyPromise;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  getItem(key: string): string | null {
    return this.cache.has(key) ? this.cache.get(key) ?? null : null;
  }

  setItem(key: string, value: string): void {
    this.cache.set(key, value);
    void this.writeEntry(key, value);
  }

  removeItem(key: string): void {
    this.cache.delete(key);
    void this.deleteEntry(key);
  }

  private async initialize(): Promise<void> {
    const database = await this.databasePromise;
    if (!database) return;

    await this.loadEntries(database);
    await this.migrateLocalStorage(database);
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);

    return new Promise((resolve) => {
      const request = indexedDB.open(this.databaseName, 1);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(this.storeName)) {
          database.createObjectStore(this.storeName, { keyPath: 'key' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
  }

  private loadEntries(database: IDBDatabase): Promise<void> {
    return new Promise((resolve) => {
      const transaction = database.transaction(this.storeName, 'readonly');
      const request = transaction.objectStore(this.storeName).getAll();

      request.onsuccess = () => {
        const entries = Array.isArray(request.result) ? request.result as StorageEntry[] : [];
        entries.forEach((entry) => {
          if (entry?.key) this.cache.set(entry.key, entry.value);
        });
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  }

  private migrateLocalStorage(database: IDBDatabase): Promise<void> {
    const entries = this.localStorageEntries();
    if (!entries.length) return Promise.resolve();

    return new Promise((resolve) => {
      const transaction = database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      entries.forEach((entry) => {
        this.cache.set(entry.key, entry.value);
        store.put(entry);
      });

      transaction.oncomplete = () => {
        this.removeMigratedLocalStorage(entries.map((entry) => entry.key));
        resolve();
      };
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  }

  private writeEntry(key: string, value: string): Promise<void> {
    return this.databasePromise.then((database) => {
      if (!database) return;

      return new Promise<void>((resolve) => {
        const transaction = database.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).put({ key, value });
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      });
    });
  }

  private deleteEntry(key: string): Promise<void> {
    return this.databasePromise.then((database) => {
      if (!database) return;

      return new Promise<void>((resolve) => {
        const transaction = database.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).delete(key);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      });
    });
  }

  private localStorageEntries(): StorageEntry[] {
    try {
      return Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
        .filter((key): key is string => !!key)
        .map((key) => ({ key, value: localStorage.getItem(key) ?? '' }));
    } catch {
      return [];
    }
  }

  private removeMigratedLocalStorage(keys: string[]): void {
    try {
      keys.forEach((key) => localStorage.removeItem(key));
    } catch {}
  }

}